import _ from 'lodash'
import axios from '@util/axios'
import store from '@store'

class AuthenticationService {
  constructor() {
    this.authenticating = false
    this.authenticated = false
    this.magicLoggedin = false
    this.requestAccessEmail = ''

    store.subscribe((_, state) => {
      this.authenticating = state.auth.authenticating
      this.magicLoggedin = state.auth.magicLoggedin
      this.requestAccessEmail = state.auth.requestAccessEmail
    })
  }

  // Unconditional SSO entry point, read on first paint so the SSO button/link
  // has a destination before anyone has typed an email (per-email routing is
  // checkSsoEnabled, below).
  async authConfig() {
    return await axios({
      url: `/auth/config/`,
      timeout: 8000
    })
  }

  async checkSsoEnabled(email) {
    return await axios({
      url: `/auth/sso/discover/`,
      method: 'POST',
      data: {
        email: email
      }
    })
  }

  // Native "set your password" (SSPR): email the user a code, then verify the
  // code + new password. On success the verify response carries the same
  // access_token/refresh_token/user as any other login (auto sign-in).
  async ssoNativeSsprStart(email) {
    return await axios({
      url: `/auth/sso/native/sspr/start/`,
      method: 'POST',
      data: {
        email: email
      }
    })
  }

  async ssoNativeSsprVerify(flow, otp, new_password) {
    return await axios({
      url: `/auth/sso/native/sspr/verify/`,
      method: 'POST',
      data: {
        flow: flow,
        otp: otp,
        new_password: new_password
      }
    })
  }

  // Authenticated "change my own password" - same underlying SSPR mechanism
  // as ssoNativeSsprStart/Verify above, but the email comes only from the
  // signed-in session server-side, never from this call. Availability is a
  // separate, free GET so opening Settings never itself emails a code - that
  // only happens once the user explicitly asks to change their password.
  async ssoNativeSelfSsprAvailability() {
    return await axios({
      url: `/auth/sso/native/sspr/self/availability/`
    })
  }

  async ssoNativeSelfSsprStart() {
    return await axios({
      url: `/auth/sso/native/sspr/self/start/`,
      method: 'POST'
    })
  }

  async ssoNativeSelfSsprVerify(flow, otp, new_password) {
    return await axios({
      url: `/auth/sso/native/sspr/self/verify/`,
      method: 'POST',
      data: {
        flow: flow,
        otp: otp,
        new_password: new_password
      }
    })
  }

  // Self-service 2FA for SSO users. Entra External ID gives its users no
  // My Account portal, so LimeIQ hosts the screen and these drive Microsoft
  // Graph server-side. A 200 with `available: false` means the signed-in user
  // is not SSO-linked - the panel simply does not apply to them.
  async ssoMfaMethods() {
    return await axios({
      url: `/auth/sso/mfa/methods/`
    })
  }

  // Step 1 of a number change: texts a code to the number given and holds it as
  // a candidate. Nothing reaches Entra yet, so calling this repeatedly (a
  // corrected typo, a resend) is safe and simply replaces the pending code.
  // 503 with code `sms_unavailable` means no SMS transport is configured.
  async ssoMfaStartPhoneVerification(phone_number) {
    return await axios({
      url: `/auth/sso/mfa/phone/`,
      method: 'POST',
      data: {
        phone_number: phone_number
      }
    })
  }

  // Step 2: the code from the text message. This is the call that actually
  // registers the number with Entra, so until it succeeds the user's existing
  // number is still the one receiving their sign-in codes.
  async ssoMfaConfirmPhoneVerification(code) {
    return await axios({
      url: `/auth/sso/mfa/phone/verify/`,
      method: 'POST',
      data: {
        code: code
      }
    })
  }

  // Abandon a verification in progress, so a stale candidate is not left behind.
  async ssoMfaCancelPhoneVerification() {
    return await axios({
      url: `/auth/sso/mfa/phone/verify/`,
      method: 'DELETE'
    })
  }

  async ssoMfaRemovePhone() {
    return await axios({
      url: `/auth/sso/mfa/phone/`,
      method: 'DELETE'
    })
  }

  async signup(data) {
    return await axios({
      url: `/auth/email/login/form/`,
      method: 'POST',
      data: _.pick(data, ['email', 'first_name', 'last_name', 'company'])
    })
  }

  async logout() {
    const response = await axios({
      url: `/auth/logout/`,
      method: 'POST',
      data: {
        refresh: store.getters.refreshToken
      }
    })

    store.dispatch('setAccessToken', null)
    store.dispatch('setRefreshToken', null)

    store.dispatch('setUser', null)

    this.authenticated = false

    return response
  }

  async approveSignup(data) {
    return await axios({
      url: `/auth/approved/${data.uid}-${data.key}/`,
      method: 'POST',
      data: {
        uidb36: data.uid,
        key: data.key
      }
    })
  }

  setRequestAccessEmail(email) {
    this.requestAccessEmail = email
  }
}

export default new AuthenticationService()
